import {
  extractMediaArtifactsFromResultText,
  normalizeTaskArtifacts,
} from "./taskArtifacts.mjs"

export function buildOpenClawTaskToolUse(overrides = {}) {
  return {
    source: "openclaw",
    toolEmoji: "✨",
    toolLabel: "Streaming…",
    status: "running",
    ...overrides,
  }
}

export function buildOpenClawTaskStartPayload(task, toolUse = buildOpenClawTaskToolUse()) {
  if (!task?.id) {
    throw new Error("OpenClaw task start payload requires a task id")
  }

  return {
    event: "start",
    task_id: task.id,
    task: {
      ...task,
      completedAt: task.completedAt ?? null,
      steps: Array.isArray(task.steps) ? task.steps : [],
      result: task.result ?? null,
      error: task.error ?? null,
      error_details: task.error_details ?? null,
      status: task.status || "running",
    },
    tool_use: toolUse,
  }
}

export function buildOpenClawTaskEndPayload({
  taskId,
  result = null,
  error = null,
  errorDetails = null,
  artifacts = [],
}) {
  if (!taskId) {
    throw new Error("OpenClaw task end payload requires a task id")
  }

  const normalizedArtifacts = normalizeTaskArtifacts([
    ...(Array.isArray(artifacts) ? artifacts : []),
    ...extractMediaArtifactsFromResultText(result),
  ])
  return {
    event: "end",
    task_id: taskId,
    result,
    error,
    error_details: errorDetails,
    ...(normalizedArtifacts.length > 0 ? { artifacts: normalizedArtifacts } : {}),
    tool_use: {
      source: "openclaw",
    },
  }
}

export function findTaskInSnapshot(snapshot, taskId) {
  if (!snapshot?.tasks || !taskId) return null

  const completed = Array.isArray(snapshot.tasks.completed)
    ? snapshot.tasks.completed.find((task) => task?.id === taskId)
    : null
  if (completed) return completed

  if (snapshot.tasks.active?.id === taskId) {
    return snapshot.tasks.active
  }

  const queued = Array.isArray(snapshot.tasks.queue)
    ? snapshot.tasks.queue.find((task) => task?.id === taskId)
    : null
  return queued || null
}

function normalizeComparableText(value) {
  return typeof value === "string"
    ? value.trim().toLowerCase().replace(/\s+/g, " ")
    : ""
}

function taskCompletedAt(task) {
  const completedAt = Number(task?.completedAt)
  return Number.isFinite(completedAt) ? completedAt : 0
}

function taskHasRenderableArtifacts(task) {
  return normalizeTaskArtifacts(task?.artifacts).length > 0
}

export function findRecentPlatformSyncedCompletion({
  tasks,
  agentIds = [],
  activeTask = null,
  minCompletedAt = 0,
  requireArtifacts = false,
}) {
  if (!Array.isArray(tasks) || tasks.length === 0) return null

  const candidateAgentIds = new Set(
    [activeTask?.agentId, ...agentIds]
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter(Boolean)
  )
  const activeTaskId =
    typeof activeTask?.id === "string" ? activeTask.id.trim() : ""
  const activeLabel = normalizeComparableText(
    activeTask?.label || activeTask?.instruction
  )
  const activeInstruction = normalizeComparableText(activeTask?.instruction)
  const floor = Number.isFinite(Number(minCompletedAt))
    ? Number(minCompletedAt)
    : 0

  const candidates = tasks
    .filter((task) => {
      if (!task || String(task.status || "").toLowerCase() !== "complete") {
        return false
      }
      if (candidateAgentIds.size > 0) {
        const taskAgentId =
          typeof task.agentId === "string" ? task.agentId.trim() : ""
        if (!candidateAgentIds.has(taskAgentId)) return false
      }
      if (taskCompletedAt(task) < floor) return false
      if (requireArtifacts && !taskHasRenderableArtifacts(task)) {
        return false
      }
      const taskId = typeof task.id === "string" ? task.id.trim() : ""
      if (activeTaskId && taskId === activeTaskId) {
        return taskHasRenderableArtifacts(task)
      }
      const taskLabel = normalizeComparableText(task.label || task.instruction)
      const taskInstruction = normalizeComparableText(task.instruction)
      return (
        taskHasRenderableArtifacts(task) ||
        (activeLabel && taskLabel === activeLabel) ||
        (activeInstruction && taskInstruction === activeInstruction)
      )
    })
    .sort((left, right) => {
      const rightArtifacts = taskHasRenderableArtifacts(right) ? 1 : 0
      const leftArtifacts = taskHasRenderableArtifacts(left) ? 1 : 0
      if (rightArtifacts !== leftArtifacts) {
        return rightArtifacts - leftArtifacts
      }
      return taskCompletedAt(right) - taskCompletedAt(left)
    })

  return candidates[0] || null
}
